/**
 * apps/web :: components/whatsnext :: Insurance101Section
 *
 * Section 2 of the post-pass journey (ROADS-431).
 *
 * Two deliverables on this card:
 *   1. Glossary of 8 core auto-insurance terms (liability, comprehensive,
 *      collision, deductible, premium, PIP, UM/UIM, SR-22). EN + ES copy
 *      lands via i18n keys; the ES glossary follows ROADS-427 conventions
 *      ("responsabilidad civil", "deducible", etc.).
 *   2. Per-state liability minimums sourced from
 *      `data/per-state-insurance.ts` (50 + DC live as of ROADS-427).
 *
 * Status-agnostic per ROADS-436: every line of copy frames a state
 * regime ("Florida requires X minimum"), never a user label.
 */
import { useTranslation } from "react-i18next";

import {
  getPerStateInsurance,
  type LocalizedString,
} from "@/data/per-state-insurance";

interface Insurance101SectionProps {
  readonly stateCode: string;
  readonly lang: "en" | "es";
}

const GLOSSARY_TERMS = [
  "liability",
  "comprehensive",
  "collision",
  "deductible",
  "premium",
  "pip",
  "umUim",
  "sr22",
] as const;

function localized(s: LocalizedString, lang: "en" | "es"): string {
  return s[lang] ?? s.en;
}

function formatUsd(amount: number): string {
  return `$${amount.toLocaleString("en-US")}`;
}

export default function Insurance101Section({
  stateCode,
  lang,
}: Insurance101SectionProps) {
  const { t } = useTranslation();
  const insurance = getPerStateInsurance(stateCode);
  const stateName = insurance ? localized(insurance.displayName, lang) : stateCode;

  return (
    <section
      data-testid="whatsnext-section-insurance"
      data-state={stateCode}
      aria-labelledby="whatsnext-insurance-heading"
      className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
    >
      <h2
        id="whatsnext-insurance-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("whatsnext.insurance.heading")}
      </h2>
      <p className="mt-sm text-neutral-700">{t("whatsnext.insurance.intro")}</p>

      {/* Glossary */}
      <h3 className="mt-md font-semibold text-brand-primary">
        {t("whatsnext.insurance.glossaryHeading")}
      </h3>
      <dl className="mt-sm divide-y divide-neutral-200" data-testid="whatsnext-insurance-glossary">
        {GLOSSARY_TERMS.map((term) => (
          <div key={term} className="py-sm" data-testid={`whatsnext-glossary-${term}`}>
            <dt className="text-sm font-semibold text-brand-primary">
              {t(`whatsnext.insurance.glossary.${term}.term`)}
            </dt>
            <dd className="mt-xs text-sm text-neutral-700">
              {t(`whatsnext.insurance.glossary.${term}.definition`)}
            </dd>
          </div>
        ))}
      </dl>

      {/* Per-state minimums */}
      <h3 className="mt-md font-semibold text-brand-primary">
        {t("whatsnext.insurance.minimumsHeading", { state: stateName })}
      </h3>
      {insurance ? (
        <ul
          className="mt-sm space-y-xs text-sm text-neutral-800"
          data-testid="whatsnext-insurance-minimums"
        >
          <li>
            {t("whatsnext.insurance.minBodilyInjuryPerPerson", {
              amount: formatUsd(insurance.minBodilyInjuryPerPerson.amountUsd),
            })}
          </li>
          <li>
            {t("whatsnext.insurance.minBodilyInjuryPerAccident", {
              amount: formatUsd(insurance.minBodilyInjuryPerAccident.amountUsd),
            })}
          </li>
          <li>
            {t("whatsnext.insurance.minPropertyDamage", {
              amount: formatUsd(insurance.minPropertyDamage.amountUsd),
            })}
          </li>
        </ul>
      ) : (
        <p className="mt-sm text-sm text-neutral-700">
          {t("whatsnext.insurance.minimumsUnavailable")}
        </p>
      )}
    </section>
  );
}
